import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ensureCatalogAuth } from "../../../services/backend/authService";
import { getCatalogo } from "../../../services/backend/catalogService";
import { useWarehouseContext } from "../../../app/providers/contextIndex";
import { mapCatalogResponseProducts, } from "../utils/catalogUtils";

const DEFAULT_PAGE_SIZE = 9999;

const normalizeCatalogAttributes = (attributeFilters = {}) => Object.fromEntries(
  Object.entries(attributeFilters).filter(([, values]) => Array.isArray(values) && values.length)
);

const useCatalogProducts = ({
  currentFamilyId = 0,
  searchQuery = "",
  refreshToken = "",
  enabled = true,
  attributeFilters = {},
}) => {
  const { selectedAlmacen } = useWarehouseContext();
  const [rawProducts, setRawProducts] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestIdRef = useRef(0);
  const normalizedAttributeFilters = useMemo(
    () => normalizeCatalogAttributes(attributeFilters),
    [attributeFilters]
  );

  const fetchCatalog = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");

    try {
      await ensureCatalogAuth();

      if (requestId !== requestIdRef.current) return;

      const response = await getCatalogo({
        almacen: selectedAlmacen,
        atributos: normalizedAttributeFilters,
        descripcion: searchQuery,
        familia: currentFamilyId,
        start: 0,
        length: DEFAULT_PAGE_SIZE,
      });

      if (requestId !== requestIdRef.current) return;

      const mappedProducts = mapCatalogResponseProducts(response);

      setRawProducts(mappedProducts);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err?.message || "No fue posible cargar el catalogo.");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
    }, [currentFamilyId, normalizedAttributeFilters, searchQuery, selectedAlmacen]);

  useEffect(() => {
    if (!enabled) {
      setRawProducts([]);
      setLoading(false);
      setError("");
      return;
    }

    fetchCatalog();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { requestIdRef.current++; };
  }, [enabled, fetchCatalog]);

  return {
    products: rawProducts,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    loading,
    error,
  };
};

export default useCatalogProducts;
